/**
 * dialogBus — the module-level singleton behind the GLOBAL blocking-dialog
 * functions (BO UI Standard §13, ruling 2026-07-05 "two-tier message standard").
 *
 * Tier 1 (BLOCKING) messages — Confirmation · Error · Warning · milestone
 * Success — render as a CENTERED MODAL (never a banner).  This bus lets any
 * code raise one imperatively, from anywhere (even non-React modules), exactly
 * like the standard's `showDialog()` / `warn()` / `errorDlg()` / `confirmDlg()`:
 *
 *   import { warn, errorDlg, confirmDlg, showDialog } from '../../components/confirm';
 *   warn('Required Fields Missing', { list: ['Incubator ID', 'Water Bath ID'] });
 *   errorDlg('Could not reach the server. Your changes were not saved.');
 *   if (await confirmDlg('Save and create the QC Batch?')) { … }
 *   await showDialog({ type: 'success', title: 'QC Batch Created',
 *     idLabel: 'QC Batch ID', idValue: 'QB260705_004',
 *     rows: [{ label: 'Status', value: 'Pending Start Setup' }],
 *     actions: [{ label: 'Start Setup →', primary: true, value: 'setup' }] });
 *
 * The single <ConfirmProvider> mounted at the app root registers the actual
 * renderer via {@link registerDialogRunner}; until then (or in isolated tests
 * with no provider) calls resolve to a safe fallback so a message is never lost.
 *
 * Tier 2 (transient Success / Info) stays on the top banner (useNotify).
 * Tier 3 (single-field validation) stays inline (React Hook Form).
 */

/** The blocking dialog types (icon + 2px coloured border per §13).  The four
 *  standard types plus `info` (a neutral blue "Notice" — used by the
 *  useConfirm().alert({ tone: 'info' }) acknowledgement). */
export type DialogType = 'confirm' | 'error' | 'warning' | 'success' | 'info';

/** A labelled detail row shown in a milestone-success dialog. */
export interface DialogRow {
  label: string;
  value: string;
}

/** A footer action button.  The dialog resolves with the clicked button's
 *  `value` (or `true` for a bare confirm / `false` when dismissed). */
export interface DialogAction {
  label: string;
  /** Orange primary styling when true (exactly one primary per dialog). */
  primary?: boolean;
  /** Resolved value when this button is clicked (defaults to the label). */
  value?: string;
}

export interface DialogRequest {
  type: DialogType;
  title?: string;
  message?: string;
  /** Bullet list (e.g. the missing required fields in a Warning). */
  list?: string[];
  /** Detail rows for a milestone-success dialog (Status / Rows / Incubator …). */
  rows?: DialogRow[];
  /** Big highlighted ID (milestone success) — e.g. "QC Batch ID" / "QB260705_004". */
  idLabel?: string;
  idValue?: string;
  /** A muted footnote under the body. */
  note?: string;
  /** Footer buttons.  Omit for the standard OK (error/warning/success/info) or
   *  Cancel/Yes (confirm) buttons. */
  actions?: DialogAction[];
  /** Override the default confirm buttons (used when useConfirm().confirm routes
   *  through here). */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Override the default OK button (used by useConfirm().alert). */
  okLabel?: string;
}

/** Resolution: a confirm resolves boolean; an actioned dialog resolves the
 *  clicked action's value; a plain OK dialog resolves true. */
export type DialogResult = boolean | string;

type DialogRunner = (req: DialogRequest) => Promise<DialogResult>;

let runner: DialogRunner | null = null;

/** Called ONCE by <ConfirmProvider> on mount to wire the real renderer. */
export function registerDialogRunner(fn: DialogRunner | null): void {
  runner = fn;
}

/** True when a modal renderer is mounted (a <ConfirmProvider> is active).
 *  Lets the banner service know whether error/warning were handed to the modal
 *  or need a banner fallback (isolated tests with no provider). */
export function dialogRunnerActive(): boolean {
  return runner !== null;
}

/** Raise a blocking dialog from anywhere.  Falls back gracefully when no
 *  provider is mounted (isolated unit tests): confirm → false, others → true,
 *  so a message is never silently lost and awaits never hang. */
export function showDialog(req: DialogRequest): Promise<DialogResult> {
  if (runner) return runner(req);
  return Promise.resolve(req.type === 'confirm' ? false : true);
}

/** Warning modal — validation / blocked action / permission guard (amber). */
export function warn(message: string, opts?: Omit<DialogRequest, 'type' | 'message'>): Promise<DialogResult> {
  return showDialog({ type: 'warning', message, ...opts });
}

/** Error modal — genuine failures only (save / network / system; red). */
export function errorDlg(message: string, opts?: Omit<DialogRequest, 'type' | 'message'>): Promise<DialogResult> {
  return showDialog({ type: 'error', message, ...opts });
}

/** Confirmation modal (Yes/No, blue) — replaces native confirm().  Runs
 *  `onYes` when confirmed and returns the boolean either way. */
export async function confirmDlg(message: string, onYes?: () => void): Promise<boolean> {
  const ok = (await showDialog({ type: 'confirm', message })) === true;
  if (ok && onYes) onYes();
  return ok;
}
